// Modal for creating a new workspace — collects a workspace name and calls
// handleCreateWorkspace from useWorkspaceHandlers.

import React from 'react';
import useMediaQuery from '../../../hooks/useMediaQuery';
import usePendingAction from '../../../hooks/usePendingAction';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../primitives/Button';

function CreateWorkspaceModal({ onClose, onCreate }) {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const [name, setName] = React.useState('');

    const { showToast } = useToast();
    // D-29: synchronous guard, so a double click or a second Enter creates one
    // team. A null result is a failure the handler already toasted; a rejection
    // is toasted here. Either way the typed name stays.
    const { run, pending } = usePendingAction(() => onCreate(name), {
        onError: () => showToast("Couldn't create the team. Try again.", 'error'),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        run();
    };

    return (
        <div className={`fixed inset-0 z-50 flex bg-black bg-opacity-50 ${isMobile ? 'items-end justify-center' : 'items-center justify-center p-4'}`}>
            <div className={`flex w-full flex-col bg-[color:var(--surface-primary)] shadow-xl ${isMobile ? 'h-[100dvh] rounded-none' : 'max-w-md rounded-lg'}`}>
                <div className={`flex items-start justify-between border-b border-[color:var(--border-color)] ${isMobile ? 'px-4 py-4' : 'px-8 py-6'}`}>
                    <h2 className="text-2xl font-bold">Create New Team</h2>
                    <button type="button" onClick={onClose} disabled={pending} className="text-2xl leading-none text-[color:var(--text-faint)] hover:text-[color:var(--text-muted)] disabled:opacity-50" aria-label="Close create team modal">
                        &times;
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <div className={`overflow-y-auto ${isMobile ? 'px-4 py-4' : 'px-8 py-6'}`}>
                        <label htmlFor="workspaceName" className="block text-sm font-medium text-[color:var(--text-secondary)]">Team Name</label>
                        <div className="mt-1">
                            <input
                                type="text"
                                id="workspaceName"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full rounded-md border p-3"
                                placeholder="e.g., 2024 Transactions"
                                readOnly={pending}
                                required
                            />
                        </div>
                    </div>
                    <div className={`border-t border-[color:var(--border-color)] ${isMobile ? 'px-4 py-4' : 'px-8 py-6'}`}>
                        <div className={`flex gap-3 ${isMobile ? 'flex-col' : 'justify-end'}`}>
                            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>Cancel</Button>
                            <Button type="submit" variant="accent" loading={pending} loadingLabel="Creating…">Create</Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateWorkspaceModal;
